import { memo, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ModalBody, ModalContent, Modal, ModalHeader, ModalFooter } from '@heroui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useCreateGroupMutation, Intake } from '@/entities/Courses';
import { AppButton } from '@/shared/ui/AppButton';
import { CustomInput, CustomSelect } from '@/shared/ui/Fields';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { useToast } from '@/shared/lib/hooks/useToast';
import styles from './AddGroupModal.module.scss';

interface AddGroupModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  intakes: Intake[];
  onSuccess?: () => void;
}

interface GroupFormData {
  intakeId: string;
  number: number;
  title: string;
}

const groupSchema = yup.object({
  intakeId: yup.string().required('Select intake is required'),
  number: yup.number().required('Group number is required').min(1).integer(),
  title: yup.string().required('Group title is required').min(1).max(255),
}) as yup.ObjectSchema<GroupFormData>;

export const AddGroupModal = memo(
  ({ isOpen, onOpenChange, intakes, onSuccess }: AddGroupModalProps) => {
    const { toast } = useToast();
    const [createGroup, { isLoading }] = useCreateGroupMutation();

    const {
      control,
      handleSubmit,
      formState: { errors },
      reset,
      watch,
    } = useForm<GroupFormData>({
      resolver: yupResolver(groupSchema) as any,
      defaultValues: {
        intakeId: '',
        number: 0,
        title: '',
      },
    });

    const intakeItems = useMemo(
      () =>
        intakes.map((intake) => ({
          key: String(intake.id),
          label: intake.title,
        })),
      [intakes],
    );

    const watchedIntakeId = watch('intakeId');
    const watchedNumber = watch('number');
    const watchedTitle = watch('title');

    const canSubmit =
      !!watchedIntakeId && !!watchedNumber && watchedNumber > 0 && !!watchedTitle?.trim();

    const onSubmit = async (data: GroupFormData) => {
      try {
        const result = await createGroup({
          intakeId: Number(data.intakeId),
          number: data.number,
          title: data.title,
        }).unwrap();

        toast({
          title: 'Success',
          description: result.message || 'Group created successfully',
          color: 'success',
        });

        handleClose();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message || 'Failed to create group',
          color: 'danger',
        });
      }
    };

    const handleClose = () => {
      reset({
        intakeId: '',
        number: 0,
        title: '',
      });
      onOpenChange(false);
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={handleClose}
        placement="center"
        motionProps={scaleInOut}
        size="2xl"
        classNames={{
          base: 'p-[36px] flex flex-col gap-5 max-w-[552px]',
          closeButton: styles.closeBtn,
        }}
      >
        <ModalContent>
          {() => (
            <form onSubmit={handleSubmit(onSubmit as any)}>
              <ModalHeader className={styles.title}>Add new group</ModalHeader>
              <ModalBody className="p-0 flex flex-col gap-5">
                <Controller
                  name="intakeId"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      style="black"
                      label="Select intake"
                      labelPlacement="outside"
                      placeholder="Select intake"
                      items={intakeItems}
                      selectedKeys={field.value ? new Set([field.value]) : new Set()}
                      onSelectionChange={(keys) => {
                        const selected = Array.from(keys)[0] as string;
                        field.onChange(selected);
                      }}
                      errorMessage={errors.intakeId?.message}
                      showError={!!errors.intakeId}
                      isRequired
                    />
                  )}
                />

                <Controller
                  name="number"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      {...field}
                      value={field.value?.toString() || ''}
                      onChange={(e) => field.onChange(Number(e.target.value) || 0)}
                      type="number"
                      label="Group Number"
                      labelPlacement="outside"
                      placeholder="Enter the group number"
                      errorMessage={errors.number?.message}
                      showError={!!errors.number}
                      isRequired
                    />
                  )}
                />

                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      {...field}
                      label="Group Title"
                      labelPlacement="outside"
                      placeholder="Advanced Group"
                      errorMessage={errors.title?.message}
                      showError={!!errors.title}
                      isRequired
                    />
                  )}
                />
              </ModalBody>
              <ModalFooter className="gap-4 pt-5 pb-0 px-0">
                <AppButton fullWidth color="white" onPress={handleClose} type="button">
                  Cancel
                </AppButton>
                <AppButton
                  fullWidth
                  color="black"
                  type="submit"
                  isLoading={isLoading}
                  disabled={!canSubmit}
                  className={!canSubmit ? styles.createButtonDisabled : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmit as any)();
                  }}
                >
                  Create group
                </AppButton>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

AddGroupModal.displayName = 'AddGroupModal';
